/* bh-family-drawer.js
 * Fullscreen cinematic stone picker (constellation layout) for BH family explorer.
 * Active stone scales + label appears; background glow shifts to stone color;
 * roman numerals counter; SVG dotted path connecting constellation positions.
 */
(function () {
  'use strict';

  const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

  function toRoman(n) {
    return ROMAN[n] || String(n);
  }

  function hexToRgbTriplet(hex) {
    if (!hex) return '200, 200, 200';
    var h = String(hex).replace('#', '').trim();
    if (h.length === 3) {
      h = h.split('').map(function (c) { return c + c; }).join('');
    }
    if (h.length !== 6) return '200, 200, 200';
    var r = parseInt(h.substr(0, 2), 16);
    var g = parseInt(h.substr(2, 2), 16);
    var b = parseInt(h.substr(4, 2), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return '200, 200, 200';
    return r + ', ' + g + ', ' + b;
  }

  /**
   * Scatter constellation positions — predefined coords per stone count,
   * arranged like real star maps (intentional asymmetry, no arc).
   */
  const POSITIONS = {
    2: [{x:30,y:40},{x:70,y:60}],
    3: [{x:25,y:30},{x:72,y:38},{x:48,y:78}],
    4: [{x:22,y:28},{x:74,y:24},{x:30,y:74},{x:78,y:72}],
    5: [{x:18,y:36},{x:55,y:18},{x:82,y:42},{x:68,y:78},{x:25,y:72}],
    6: [{x:14,y:32},{x:48,y:14},{x:82,y:28},{x:88,y:64},{x:52,y:82},{x:18,y:70}],
    7: [{x:12,y:30},{x:42,y:12},{x:75,y:22},{x:90,y:55},{x:68,y:82},{x:35,y:78},{x:15,y:60}],
  };

  function constellationPos(idx, total) {
    if (total <= 1) return { x: 50, y: 50 };
    const layout = POSITIONS[total] || POSITIONS[6];
    return layout[idx % layout.length];
  }

  class BhFamilyDrawer extends HTMLElement {
    constructor() {
      super();
      this.payload = null;
      this.currentIdx = 0;
      this.lastTrigger = null;
      this.scrollY = 0;
      this.ctaTemplate = this.dataset.ctaTemplate || 'Select {STONE}';
      this.materialSilver = this.dataset.materialSilver || 'Sterling silver 925 · Vulcan oxidized';
      this.materialGold = this.dataset.materialGold || 'Sterling silver 925 · 24kt gold plated · Vulcan oxidized';
      this.stoneLabel = this.dataset.stoneLabel || 'STONE';
      this._imageSwapTimeout = null;
    }

    _stoneMeaning(stoneName) {
      if (!stoneName) return '';
      const lower = stoneName.toLowerCase().trim();
      const knownStones = ['sanguinis', 'nox', 'lux', 'abyssus', 'vesper', 'viridis', 'umbra'];
      const match = knownStones.find(s => lower.includes(s));
      if (!match) return '';
      const attr = 'meaning' + match.charAt(0).toUpperCase() + match.slice(1);
      return this.dataset[attr] || '';
    }

    connectedCallback() {
      this.backdrop = this.querySelector('[data-bh-drawer-backdrop]');
      this.sheet = this.querySelector('.bh-family-drawer__sheet');
      this.scrollEl = this.querySelector('[data-bh-drawer-scroll]');
      this.glowEl = this.querySelector('[data-bh-glow]');
      this.titleEl = this.querySelector('[data-bh-family-name]');
      this.imageEl = this.querySelector('[data-bh-stone-image]');
      this.imageElB = this.querySelector('[data-bh-stone-image-b]');
      this.contentCol = this.querySelector('.bh-family-drawer__col--content');
      this.stoneNameEl = this.querySelector('[data-bh-stone-name]');
      this.narrativeEl = this.querySelector('[data-bh-stone-narrative]');
      this.pickerEl = this.querySelector('[data-bh-stone-picker]');
      this.constSvg = this.querySelector('[data-bh-constellation-svg]');
      this.counterCurrentEl = this.querySelector('[data-bh-stone-current]');
      this.counterTotalEl = this.querySelector('[data-bh-stone-total]');
      this.priceEl = this.querySelector('[data-bh-price]');
      this.materialEl = this.querySelector('[data-bh-material]');
      this.ctaEl = this.querySelector('[data-bh-cta]');
      this.ctaFamilyEl = this.querySelector('[data-bh-cta-family]');
      this.closeBtn = this.querySelector('[data-bh-drawer-close]');
      this.closeBtns = this.querySelectorAll('[data-bh-drawer-close]');
      this.markFamilyEl = this.querySelector('[data-bh-mark-family]');
      this.markCounterEl = this.querySelector('[data-bh-mark-counter]');

      this._bindCardDelegation();
      this._bindDismissHandlers();
      this._bindCtaTransition();
      this._bindKeyboard();

      window.openBHFamilyDrawer = (anchorHandle) => {
        const trigger = document.querySelector(`[data-bh-family-anchor="${anchorHandle}"]`);
        if (trigger) trigger.click();
      };
    }

    _bindCardDelegation() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-bh-family-anchor]');
        if (!trigger) return;
        const payloadEl = trigger.querySelector('script[data-bh-family-payload]');
        if (!payloadEl) return;
        e.preventDefault();
        try {
          const payload = JSON.parse(payloadEl.textContent);
          this.lastTrigger = trigger;
          this._launchFromCard(trigger, payload);
        } catch (err) {
          console.error('[bh-family-drawer] invalid payload', err);
          if (trigger.href) window.location.href = trigger.href;
        }
      });
    }

    _launchFromCard(trigger, payload) {
      const card = trigger.classList.contains('bh-family-card')
        ? trigger
        : trigger.querySelector('.bh-family-card') || trigger;
      card.classList.add('is-launching');

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = reduceMotion ? 0 : 180;

      setTimeout(() => {
        this.open(payload);
        setTimeout(() => card.classList.remove('is-launching'), 500);
      }, delay);
    }

    _bindDismissHandlers() {
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());
      if (this.closeBtns && this.closeBtns.length) {
        this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));
      } else if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
    }

    _bindCtaTransition() {
      if (!this.ctaEl) return;
      this.ctaEl.addEventListener('click', (e) => {
        if (!this.ctaEl.href) return;
        if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          e.preventDefault();
          const url = this.ctaEl.href;
          document.startViewTransition(() => { window.location.href = url; });
        }
      });
    }

    _bindKeyboard() {
      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key === 'Tab') { this._trapFocus(e); return; }
        if (e.target.closest('[data-bh-stone-picker]')) {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            this._cycleStone(1);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            this._cycleStone(-1);
          }
        }
      });
    }

    _trapFocus(e) {
      const focusable = this.querySelectorAll(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    _cycleStone(direction) {
      if (!this.payload) return;
      const count = this.payload.stones.length;
      const next = (this.currentIdx + direction + count) % count;
      this.selectStone(next);
      const btn = this.pickerEl.querySelector(`[data-idx="${next}"]`);
      if (btn) btn.focus();
    }

    open(payload) {
      this.payload = payload;
      this._populate();
      this.hidden = false;
      void this.offsetWidth;
      this.classList.add('is-open');
      this._lockBodyScroll();
      requestAnimationFrame(() => {
        if (this.ctaEl) this.ctaEl.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      this.sheet.style.transform = '';
      this._unlockBodyScroll();
      const onEnd = (ev) => {
        if (ev.target !== this.sheet) return;
        this.sheet.removeEventListener('transitionend', onEnd);
        this.hidden = true;
        if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
          this.lastTrigger.focus({ preventScroll: true });
        }
      };
      this.sheet.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!this.classList.contains('is-open') && !this.hidden) this.hidden = true;
      }, 800);
    }

    _populate() {
      if (!this.payload) return;
      this.titleEl.textContent = this.payload.familyName || '';
      if (this.markFamilyEl) this.markFamilyEl.textContent = this.payload.familyName || '';
      if (this.counterTotalEl) this.counterTotalEl.textContent = toRoman(this.payload.stones.length);
      // Swap material line: gold-plated text for GOLDEN family, silver otherwise
      if (this.materialEl) {
        var famLower = (this.payload.familyName || '').toLowerCase();
        var isGold = famLower.indexOf('golden') !== -1 || famLower.indexOf('gold') !== -1;
        this.materialEl.textContent = isGold ? this.materialGold : this.materialSilver;
      }
      this._renderConstellation();
      this.selectStone(0, true);
      if (this.ctaFamilyEl && this.payload.stones[0]) {
        this.ctaFamilyEl.href = this.payload.stones[0].url;
      }
    }

    _renderConstellation() {
      // Remove old dots
      this.pickerEl.querySelectorAll('.bh-family-drawer__dot').forEach((el) => el.remove());

      this.payload.stones.forEach((stone, idx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bh-family-drawer__dot';
        btn.style.setProperty('--c', stone.color || '#888');
        btn.style.setProperty('--stagger-delay', (idx * 60 + 450) + 'ms');
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-label', stone.name);
        btn.setAttribute('aria-checked', 'false');
        btn.dataset.idx = String(idx);

        const swatch = document.createElement('span');
        swatch.className = 'bh-family-drawer__dot-swatch';
        btn.appendChild(swatch);

        const label = document.createElement('span');
        label.className = 'bh-family-drawer__dot-name';
        label.textContent = (stone.name || '').toUpperCase();
        btn.appendChild(label);

        btn.addEventListener('click', () => this.selectStone(idx));
        this.pickerEl.appendChild(btn);
      });

      if (this.constSvg) this.constSvg.innerHTML = '';
    }

    selectStone(idx, instant) {
      const stone = this.payload && this.payload.stones[idx];
      if (!stone) return;
      this.currentIdx = idx;

      // Update dot active state
      this.pickerEl.querySelectorAll('.bh-family-drawer__dot').forEach((d, i) => {
        const active = i === idx;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-checked', active ? 'true' : 'false');
        d.tabIndex = active ? 0 : -1;
      });

      // Update glow color (RGB triplet for CSS rgb(var(--bh-glow-color) / alpha))
      this.style.setProperty('--bh-glow-color', hexToRgbTriplet(stone.color));
      if (this.glowEl) {
        this.glowEl.style.setProperty('--glow', stone.color || '#888');
      }
      if (this.counterCurrentEl) {
        this.counterCurrentEl.textContent = toRoman(idx + 1);
      }
      if (this.markCounterEl) {
        const total = this.payload.stones.length;
        this.markCounterEl.textContent = toRoman(idx + 1) + '/' + toRoman(total);
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (instant || reduceMotion) {
        this._applyText(stone);
        if (stone.image && this.imageEl) {
          this.imageEl.src = stone.image;
          this.imageEl.alt = stone.name || '';
          this.imageEl.dataset.currentSrc = stone.image;
          this.imageEl.classList.add('is-active');
          if (this.imageElB) this.imageElB.classList.remove('is-active');
        }
        return;
      }

      this._crossfadeImage(stone);
      this._fadeText(stone);
    }

    _applyText(stone) {
      if (this.stoneNameEl) this.stoneNameEl.textContent = stone.name || '';
      if (this.narrativeEl) {
        this.narrativeEl.textContent = this._stoneMeaning(stone.name) || stone.narrative || '';
      }
      if (this.priceEl) this.priceEl.textContent = stone.price || '';
      if (this.ctaEl) {
        this.ctaEl.href = stone.url;
        this.ctaEl.textContent = this.ctaTemplate.replace('{STONE}', stone.name || '');
      }
    }

    _fadeText(stone) {
      if (!this.contentCol) {
        this._applyText(stone);
        return;
      }
      clearTimeout(this._textSwapTimeout);
      this.contentCol.classList.add('is-swapping');
      this._textSwapTimeout = setTimeout(() => {
        this._applyText(stone);
        requestAnimationFrame(() => this.contentCol.classList.remove('is-swapping'));
      }, 130);
    }

    _crossfadeImage(stone) {
      if (!stone.image || !this.imageEl) return;

      // Single-image fallback if layer B not present
      if (!this.imageElB) {
        this.imageEl.src = stone.image;
        this.imageEl.alt = stone.name || '';
        return;
      }

      const a = this.imageEl;
      const b = this.imageElB;
      const activeLayer = a.classList.contains('is-active') ? a : b;
      const otherLayer = activeLayer === a ? b : a;

      // Same image already loaded on inactive layer? Just toggle.
      if (otherLayer.dataset.currentSrc === stone.image) {
        activeLayer.classList.remove('is-active');
        otherLayer.classList.add('is-active');
        return;
      }

      const preloader = new Image();
      const swap = () => {
        otherLayer.src = stone.image;
        otherLayer.alt = stone.name || '';
        otherLayer.dataset.currentSrc = stone.image;
        requestAnimationFrame(() => {
          activeLayer.classList.remove('is-active');
          otherLayer.classList.add('is-active');
        });
      };
      preloader.onload = swap;
      preloader.onerror = swap;
      preloader.src = stone.image;
    }

    _lockBodyScroll() {
      this.scrollY = window.scrollY || window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    _unlockBodyScroll() {
      const y = this.scrollY;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (typeof y === 'number') window.scrollTo(0, y);
    }
  }

  if (!customElements.get('bh-family-drawer')) {
    customElements.define('bh-family-drawer', BhFamilyDrawer);
  }
})();
